import { createAsyncThunk } from '@reduxjs/toolkit';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { additionalQueryParams } from '../../utils/utilities';
import axios from 'axios';
import { BaseCDRListItemType } from '../../types/cdr/base-cdr.type';
import { rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';
import { getCSRPCdrsListPath, getMegaSwitchCdrsListPath, getNetsapiensCdrsListPath } from '../../routes/Router';

export const getCDRsList = createAsyncThunk<
  PaginationType<BaseCDRListItemType>,
  RouteParamsType & { controller?: AbortController },
  { state: RootReducerState }
>('cdrs/getCDRsList/no-loader', async (data, thunkAPI) => {
  const { controller, ...params } = data;
  const pathname = window.location.pathname;
  params.type = 'Netsapiens';

  if (pathname === getNetsapiensCdrsListPath) {
    params.type = 'Netsapiens';
  } else if (pathname === getMegaSwitchCdrsListPath) {
    params.type = 'MegaSwitch';
  } else if (pathname === getCSRPCdrsListPath) {
    params.type = 'CSRP';
  }

  try {
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs
    );
    const mergedParams = { ...params, ...additionalParams };

    const response = await axios.get<PaginationType<BaseCDRListItemType>>(
      `${process.env.REACT_APP_API_URL}/cdrs`,
      controller ? { signal: controller.signal, params: mergedParams } : { params: mergedParams }
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching cdrs!' });
  }
});

export const getATACDRsList = createAsyncThunk<
  PaginationType<BaseCDRListItemType>,
  RouteParamsType & { dids?: string[] } & { controller?: AbortController },
  { state: RootReducerState }
>('cdrs/getATACDRsList/no-loader', async (data, thunkAPI) => {
  const { acsATADetails, gsATADetails } = thunkAPI.getState().atasReducer;
  const { controller, ...params } = data;

  let dids: string[] = [];
  if (acsATADetails) {
    dids = acsATADetails?.acs_ata_ports?.flatMap(port => port?.pbxExtension?.dids?.map(d => d.did) || []) || [];
  }

  if (gsATADetails) {
    dids = gsATADetails?.ata_ports?.flatMap(port => port?.pbxExtension?.dids?.map(d => d.did) || []) || [];
  }

  try {
    const response = await axios.get<PaginationType<BaseCDRListItemType>>(
      `${process.env.REACT_APP_API_URL}/cdrs`,
      controller ? { signal: controller.signal, params: { ...params, dids } } : { params: { ...params, dids } }
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching cdrs!' });
  }
});

export const getCDRMonitorLink = createAsyncThunk<
  PaginationType<BaseCDRListItemType>,
  { id: number; type?: string; cdrsType: 'Netsapiens' | 'MegaSwitch' | 'CSRP' },
  {
    state: RootReducerState;
  }
>('cdrs/getCDRMonitorLink', async ({ id, type, cdrsType }, thunkAPI) => {
  try {
    const response = await axios.get<PaginationType<BaseCDRListItemType>>(
      `${process.env.REACT_APP_API_URL}/cdrs/${id}/getMonitorDeeplink${type ? `?type=${type}` : ''}`
    );

    if (cdrsType === 'Netsapiens') {
      thunkAPI.dispatch(getCDRsList(thunkAPI.getState().cdrsReducer.netsapiensCDRsRouteParams));
    }

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching cdr deep monitor link!' });
  }
});
